const RECENT_LOCALIZATION_LANGUAGES_KEY = 'air.localization.recentLanguages';
const MAX_RECENT_LOCALIZATION_LANGUAGES = 5;

const normalizeLanguageKey = (language: string): string => language.trim().toLowerCase();

export const getRecentLocalizationLanguages = (): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(RECENT_LOCALIZATION_LANGUAGES_KEY);

    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  } catch {
    return [];
  }
};

/**
 * Moves `language` to the front of the stored recent list (most-recent-first, capped).
 * Recency decides *which* languages are kept, not the order they're shown in — see
 * `getVisibleRecentLocalizationLanguages`.
 */
export const recordRecentLocalizationLanguage = (language: string): string[] => {
  const trimmed = language.trim();

  if (!trimmed) {
    return getRecentLocalizationLanguages();
  }

  const normalized = normalizeLanguageKey(trimmed);
  const nextRecent = [
    trimmed,
    ...getRecentLocalizationLanguages().filter((entry) => normalizeLanguageKey(entry) !== normalized),
  ].slice(0, MAX_RECENT_LOCALIZATION_LANGUAGES);

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(RECENT_LOCALIZATION_LANGUAGES_KEY, JSON.stringify(nextRecent));
  }

  return nextRecent;
};

/**
 * The recent languages in display order: alphabetical rather than most-recent-first, so a pill
 * stays where the user last saw it instead of jumping to the front of the row every time it's
 * used. Only the membership of this list moves with recency.
 */
export const getVisibleRecentLocalizationLanguages = (): string[] =>
  [...getRecentLocalizationLanguages()].sort((a, b) => a.localeCompare(b));
